import type { Message } from '../flora/flora.type';
import { getToolVendor } from '../flora/components/chat/agent-tool-call-group.helpers';

/**
 * Vendor labels whose completed tool calls change a Google file's *content*, so
 * a matching call should refresh that file's read-only preview iframe. Google
 * Drive is intentionally excluded: it manages files (rename/move/copy/search)
 * rather than editing content, and a freshly created file already carries a new
 * id that remounts the iframe on its own.
 */
const GOOGLE_FILE_EDIT_VENDORS: ReadonlySet<string> = new Set(['Google Slides', 'Google Docs', 'Google Sheets']);

/**
 * Whole-segment read-only action verbs. A Google tool whose name contains one of
 * these (as a segment bounded by start/end or `.`/`_`) is treated as a read that
 * did not change content, so it must not trigger a preview refresh. Matching is
 * fail-open: any name without a recognized read verb is treated as an edit, so a
 * new/renamed backend edit tool still refreshes rather than being silently
 * missed.
 */
const READ_ACTION_PATTERN = /(?:^|[._])(get|read|list|search|fetch|export|view|download)(?:$|[._])/i;

/**
 * Determine whether a Google tool call only reads the file (vs. edits it).
 *
 * @param {string} toolName - The raw `tool_name` from a tool call.
 * @returns {boolean} True when the name reads like a read-only action.
 */
function isReadOnlyGoogleTool(toolName: string): boolean {
  return READ_ACTION_PATTERN.test(toolName);
}

/**
 * Compute a monotonic revision number per Google file id, incremented once for
 * each completed Google editing tool call that references that id. The document
 * viewer folds the revision into the iframe `key` (to remount the frame) and a
 * `src` cache-buster (to re-fetch a fresh preview rather than the browser-cached
 * pre-edit copy) — the file id alone cannot detect an in-place edit because it
 * never changes.
 *
 * A tool call is attributed to a file when the file id appears anywhere in the
 * call's serialized input or output, so it works whether the backend passes the
 * id as `presentationId`, `fileId`, or embeds it only in a returned URL.
 * Read-only Google tools ({@link isReadOnlyGoogleTool}) are skipped so routine
 * lookups do not flicker the preview.
 *
 * @param {Message[]} messages - The conversation messages holding tool calls.
 * @param {string[]} fileIds - The referenced Google file ids to track.
 * @returns {Record<string, number>} File id → revision count (0 when untouched).
 */
function computeDocumentRevisions(messages: Message[], fileIds: string[]): Record<string, number> {
  const revisions: Record<string, number> = {};
  for (const fileId of fileIds) {
    revisions[fileId] = 0;
  }
  if (fileIds.length === 0) {
    return revisions;
  }

  for (const message of messages) {
    for (const toolCall of message.toolCalls ?? []) {
      // Only a successfully completed edit produces new content — pending calls
      // have not run yet and errored calls left the file unchanged.
      if (toolCall.status !== 'completed') {
        continue;
      }
      if (!GOOGLE_FILE_EDIT_VENDORS.has(getToolVendor(toolCall.tool_name))) {
        continue;
      }
      // A read leaves content unchanged; refreshing on it only flickers the frame.
      if (isReadOnlyGoogleTool(toolCall.tool_name)) {
        continue;
      }
      const haystack = `${JSON.stringify(toolCall.tool_input)}\n${toolCall.tool_output ?? ''}`;
      for (const fileId of fileIds) {
        if (haystack.includes(fileId)) {
          revisions[fileId] += 1;
        }
      }
    }
  }
  return revisions;
}

interface DocumentLinkCacheParams {
  isActiveQuestion: boolean;
  conversationKey: string;
  cachedConversationKey: string | null;
  completedToolCallCount: number;
  cachedCompletedToolCallCount: number;
}

/**
 * A streaming response replaces its messages on every text chunk. Reuse the
 * previous document scan only while that stream belongs to the same
 * conversation and no tool call has settled since the scan.
 */
function shouldReuseDocumentLinks({
  isActiveQuestion,
  conversationKey,
  cachedConversationKey,
  completedToolCallCount,
  cachedCompletedToolCallCount,
}: DocumentLinkCacheParams): boolean {
  return (
    isActiveQuestion &&
    cachedConversationKey === conversationKey &&
    cachedCompletedToolCallCount === completedToolCallCount
  );
}

export { computeDocumentRevisions, isReadOnlyGoogleTool, shouldReuseDocumentLinks };
export type { DocumentLinkCacheParams };
